import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  Avatar,
  Switch,
  Tooltip,
  Menu,
  TableRow,
  Skeleton,
  TableHead,
  TableBody,
  TableCell,
  Dialog,
  IconButton,
  Typography,
  TableContainer,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { baseURL } from "store/services/base.config";
import {
  createSubscriptionAdmin,
  fetchPlans,
  subscribeCompanyToPlanAdmin,
} from "store/services/plan.service";
import { createAlert } from "store/utils/alertUtils";
import { normalizePlan } from "store/utils/planUtils";

const formatDate = (dateString) => {
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return dateString;
  }
};

const getInitials = (name = "") => {
  const [first = "", second = ""] = name.split(" ");
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
};

const CompanyList = ({
  companies = [],
  loading,
  onView,
  onToggleStatus,
  statusUpdatingId,
  onPlanChanged,
  title,
  searchQuery = "",
  onSearchChange,
}) => {
  const { t } = useTranslation();
  const rows = useMemo(
    () => {
      const sourceRows = companies?.length ? companies : [];
      const query = searchQuery.trim().toLowerCase();
      if (!query) return sourceRows;

      const compactQuery = query.replace(/[\s()-]/g, "");
      return sourceRows.filter((company) => {
        const searchableValues = [
          company?.company_name,
          company?.company_legal_name,
          company?.company_legal_name_arabic,
          company?.id,
          company?.company_owner_email,
          company?.company_owner_mobile,
        ].map((value) => String(value ?? "").toLowerCase());

        return searchableValues.some(
          (value) =>
            value.includes(query) ||
            value.replace(/[\s()-]/g, "").includes(compactQuery)
        );
      });
    },
    [companies, searchQuery]
  );
  const displayTitle = title || t("nav.company"); // Default title if not provided
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuCompany, setMenuCompany] = useState(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansAudience, setPlansAudience] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedSubscriptionType, setSelectedSubscriptionType] =
    useState("monthly");
  const [subscriptionAction, setSubscriptionAction] = useState("change");
  const [savingPlan, setSavingPlan] = useState(false);
  const getCompanyPlanName = (company) => {
    const subscription = company?.subscriptions?.[0];
    return (
      subscription?.plan?.name ||
      subscription?.plan_name ||
      subscription?.plan?.plan_name ||
      subscription?.name ||
      "-"
    );
  };
  const currentPlanName = getCompanyPlanName(menuCompany);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= rows.length) {
      setPage(0);
    }
  }, [page, rows.length, rowsPerPage, searchQuery]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [page, rows, rowsPerPage]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCompanyId = (company) =>
    company?.id || company?.company_id || company?.companyId;

  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event, company) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuCompany(company);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleViewCompany = () => {
    if (menuCompany) {
      onView?.(menuCompany);
    }
    handleMenuClose();
  };

  const handleNavigate = (menuCompany) => {
    onView?.(menuCompany);
  };

  const getCompanyAudience = (company) =>
    company?.is_supplier || company?.company_type === "SUPPLIER"
      ? "supplier"
      : "fleet";

  const loadPlans = async (audience) => {
    setPlansLoading(true);
    try {
      const response = await fetchPlans({
        is_superAdmin: true,
        audience,
      });
      const filteredPlans = Array.isArray(response)
        ? response
            .map((plan) => normalizePlan(plan))
            .filter((plan) => plan.audience === audience)
        : [];
      setPlans(filteredPlans);
      setPlansAudience(audience);
    } catch (error) {
      setPlans([]);
      createAlert({
        type: "error",
        status: true,
        notification: {
          title:
            error?.response?.data?.message ||
            t("companyList.dialog.fetchPlansError", {
              defaultValue: "Unable to load plans.",
            }),
          body:
            error?.response?.data?.message ||
            t("companyList.dialog.fetchPlansError", {
              defaultValue: "Unable to load plans.",
            }),
          titleArabic:
            error?.response?.data?.message ||
            t("companyList.dialog.fetchPlansError", {
              defaultValue: "Unable to load plans.",
            }),
          bodyArabic:
            error?.response?.data?.message ||
            t("companyList.dialog.fetchPlansError", {
              defaultValue: "Unable to load plans.",
            }),
        },
      });
    } finally {
      setPlansLoading(false);
    }
  };

  const openSubscriptionDialog = async (action) => {
    const audience = getCompanyAudience(menuCompany);
    handleMenuClose();
    setSubscriptionAction(action);
    setPlanDialogOpen(true);
    if (!plans.length || plansAudience !== audience) {
      await loadPlans(audience);
    }
  };

  const handleChangePlanClick = () => openSubscriptionDialog("change");
  const handleCreateSubscriptionClick = () =>
    openSubscriptionDialog("create");

  useEffect(() => {
    if (!planDialogOpen || plansLoading) return;
    if (!plans.length) {
      setSelectedPlanId("");
      return;
    }
    const currentPlanId =
      menuCompany?.subscriptions?.[0]?.plan?.id ||
      menuCompany?.subscriptions?.[0]?.plan_id;
    setSelectedPlanId(currentPlanId || plans[0]?.id || "");
    setSelectedSubscriptionType(
      menuCompany?.subscriptions?.[0]?.subscription_type ||
        menuCompany?.subscriptions?.[0]?.plan?.type ||
        plans.find((plan) => String(plan.id) === String(currentPlanId))?.type ||
        plans[0]?.type ||
        "monthly"
    );
  }, [menuCompany, planDialogOpen, plans, plansLoading]);

  const handlePlanDialogClose = () => {
    if (savingPlan) return;
    setPlanDialogOpen(false);
  };

  const handleSubmitPlanChange = async () => {
    const companyId = getCompanyId(menuCompany);
    if (!companyId || !selectedPlanId) return;
    const selectedPlan = plans.find(
      (plan) => String(plan.id) === String(selectedPlanId)
    );
    if (!selectedPlan) return;
    setSavingPlan(true);
    try {
      const subscriptionPayload = {
        companyId,
        planId: selectedPlan.id,
        subscription_type:
          subscriptionAction === "create"
            ? selectedSubscriptionType
            : selectedPlan.type || selectedPlan.subscription_type,
      };
      const response =
        subscriptionAction === "create"
          ? await createSubscriptionAdmin(subscriptionPayload)
          : await subscribeCompanyToPlanAdmin(subscriptionPayload);
      if (response?.isSuccess) {
        createAlert({
          type: "success",
          status: true,
          notification: {
            title: response?.message || t("companyList.dialog.changeSuccess"),
            body: response?.message || t("companyList.dialog.changeSuccess"),
            titleArabic:
              response?.message || t("companyList.dialog.changeSuccess"),
            bodyArabic:
              response?.message || t("companyList.dialog.changeSuccess"),
          },
        });
        setPlanDialogOpen(false);
        onPlanChanged?.(menuCompany);
      } else {
        createAlert({
          type: "error",
          status: true,
          notification: {
            title: response?.message || t("companyList.dialog.changeError"),
            body: response?.message || t("companyList.dialog.changeError"),
            titleArabic:
              response?.message || t("companyList.dialog.changeError"),
            bodyArabic:
              response?.message || t("companyList.dialog.changeError"),
          },
        });
      }
    } catch (error) {
      createAlert({
        type: "error",
        status: true,
        notification: {
          title:
            error?.response?.data?.message ||
            t("companyList.dialog.changeError", {
              defaultValue: "Failed to change plan.",
            }),
          body:
            error?.response?.data?.message ||
            t("companyList.dialog.changeError", {
              defaultValue: "Failed to change plan.",
            }),
          titleArabic:
            error?.response?.data?.message ||
            t("companyList.dialog.changeError", {
              defaultValue: "Failed to change plan.",
            }),
          bodyArabic:
            error?.response?.data?.message ||
            t("companyList.dialog.changeError", {
              defaultValue: "Failed to change plan.",
            }),
        },
      });
    } finally {
      setSavingPlan(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        px={3}
        py={2}
        borderBottom={1}
        borderColor="divider"
      >
        <Typography variant="h6" fontWeight={600}>
          {displayTitle}
        </Typography>
        <TextField
          size="small"
          value={searchQuery}
          onChange={(event) => onSearchChange?.(event.target.value)}
          placeholder={t("companyList.search", {
            defaultValue: "Search by company, ID, email or phone",
          })}
          aria-label={t("companyList.searchAriaLabel")}
          InputProps={{
            startAdornment: (
              <SearchOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            ),
          }}
          sx={{ width: { xs: "100%", sm: 360 } }}
        />
        <Typography variant="body2" color="text.secondary">
          {t("companyList.showing_companies", { count: rows.length })}
        </Typography>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 960 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t("companyList.company")}</TableCell>
              <TableCell>{t("companyList.owner")}</TableCell>
              <TableCell>{t("companyList.email")}</TableCell>
              <TableCell>{t("companyList.phone")}</TableCell>
              <TableCell>{t("companyList.plan")}</TableCell>
              <TableCell>{t("companyList.status")}</TableCell>
              <TableCell>{t("companyList.joined")}</TableCell>
              <TableCell align="left">{t("companyList.actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading
              ? Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Skeleton variant="rectangular" width={100} height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={120} height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={120} height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={60} height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={60} height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={60} height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={60} height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={60} height={40} />
                  </TableCell>
                </TableRow>
              ))
              : pagedRows.map((company) => (
                <TableRow key={company?.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        variant="circular"
                        src={getCompanyLogo(company)}
                      >
                        {getInitials(company?.company_name)}
                      </Avatar>
                      <Box
                        onClick={() => handleNavigate(company)}
                        sx={{
                          cursor: "pointer",
                          ":hover": {
                            color: "blue",
                          },
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {company?.company_name}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{`${company?.company_owner_first_name} ${company?.company_owner_last_name}`}</TableCell>
                  <TableCell>{company?.company_owner_email}</TableCell>
                  <TableCell>{company?.company_owner_mobile}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={getCompanyPlanName(company)}
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={
                          company?.is_active
                            ? t("companyList.active")
                            : t("companyList.inactive")
                        }
                        color={company?.is_active ? "success" : "default"}
                        sx={{ textTransform: "capitalize" }}
                      />
                      <Switch
                        size="small"
                        color="success"
                        checked={Boolean(company?.is_active)}
                        disabled={statusUpdatingId === company?.id}
                        onChange={() => onToggleStatus?.(company)}
                        inputProps={{
                          "aria-label": t("companyList.toggleStatusAriaLabel", {
                            name: company?.company_name,
                          }),
                        }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDate(company?.created_at)}</TableCell>
                  <TableCell align="left" style={{ width: 70 }}>
                    <Tooltip title={t("companyList.actions")}>
                      <IconButton
                        size="small"
                        aria-label={t("companyList.actions")}
                        onClick={(event) => handleMenuOpen(event, company)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

            {!rows.length && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t("companyList.no_companies_found")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t("companyList.pagination.rowsPerPage", {
          defaultValue: "Rows per page",
        })}
        labelDisplayedRows={({ from, to, count }) =>
          t("companyList.pagination.displayedRows", {
            defaultValue:
              count !== -1
                ? `${from}-${to} of ${count}`
                : `${from}-${to} of more than ${to}`,
            from,
            to,
            count,
          })
        }
        rowsPerPageOptions={[5, 10, 25]}
      />
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleViewCompany}>
          <VisibilityOutlinedIcon fontSize="small" />
          <Box component="span" sx={{ ml: 1 }}>
            {t("companyList.menu.view", { defaultValue: "View" })}
          </Box>
        </MenuItem>
        <MenuItem onClick={handleChangePlanClick}>
          <AutorenewOutlinedIcon fontSize="small" />
          <Box component="span" sx={{ ml: 1 }}>
            {t("companyList.menu.changePlan", { defaultValue: "Change plan" })}
          </Box>
        </MenuItem>
        <MenuItem onClick={handleCreateSubscriptionClick}>
          <AddCircleOutlineIcon fontSize="small" />
          <Box component="span" sx={{ ml: 1 }}>
            {t("companyList.dialogExtended.createSubscription", {
              defaultValue: "Create subscription",
            })}
          </Box>
        </MenuItem>
      </Menu>
      <Dialog
        open={planDialogOpen}
        onClose={handlePlanDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {subscriptionAction === "create"
            ? t("companyList.dialogExtended.createTitle", {
                defaultValue: "Create subscription",
              })
            : t("companyList.dialog.title", { defaultValue: "Change Plan" })}
        </DialogTitle>
        <DialogContent dividers>
          {plansLoading ? (
            <Typography variant="body2" color="text.secondary">
              {t("companyList.dialog.loading", {
                defaultValue: "Loading plans...",
              })}
            </Typography>
          ) : plans.length ? (
            <Box>
              <Stack sx={{ mb: 3 }} direction="row" gap={2} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {t("companyList.dialog.currentPlanLabel", {
                    defaultValue: "Currently selected",
                  })}{" "}
                  :
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={currentPlanName}
                    sx={{ textTransform: "capitalize" }}
                  />
                </Box>
              </Stack>
              <TextField
                select
                fullWidth
                label={t("companyList.dialog.selectPlan", {
                  defaultValue: "Select plan",
                })}
                value={selectedPlanId}
                onChange={(event) => setSelectedPlanId(event.target.value)}
              >
                {plans.map((plan) => {
                  const label =
                    plan?.name || plan?.plan_name || plan?.plan?.name || "-";
                  return (
                    <MenuItem key={plan.id} value={plan.id}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
              {subscriptionAction === "create" && (
                <>
                  <TextField
                    select
                    fullWidth
                    sx={{ mt: 2 }}
                    label={t("companyList.dialogExtended.subscriptionType", {
                      defaultValue: "Subscription type",
                    })}
                    value={selectedSubscriptionType}
                    onChange={(event) =>
                      setSelectedSubscriptionType(event.target.value)
                    }
                  >
                    <MenuItem value="monthly">
                      {t("companyList.dialogExtended.monthly")}
                    </MenuItem>
                    <MenuItem value="yearly">
                      {t("companyList.dialogExtended.yearly")}
                    </MenuItem>
                  </TextField>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 2 }}
                  >
                    {t("companyList.dialogExtended.directApplyHelp", {
                      defaultValue:
                        "This applies the subscription directly without using the payment gateway.",
                    })}
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("companyList.dialog.noPlans", {
                defaultValue: "No plans available.",
              })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePlanDialogClose} disabled={savingPlan}>
            {t("companyList.dialog.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitPlanChange}
            disabled={savingPlan || !selectedPlanId}
          >
            {savingPlan
              ? t("companyList.dialog.saving", { defaultValue: "Saving..." })
              : subscriptionAction === "create"
              ? t("companyList.dialogExtended.apply", {
                  defaultValue: "Apply subscription",
                })
              : t("companyList.dialog.save", { defaultValue: "Save" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CompanyList;
export const getCompanyLogo = (company) => {
  const logo = company?.documents
    ?.filter((doc) => doc.file_type === "logo_image")
    ?.at(-1)?.file_path; // latest logo
  if (!logo) return "";

  return `${baseURL}${logo}`;
};
